'use client';

import { useState, useEffect, useCallback } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/lib/context/AuthContext';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useToast } from '@/lib/context/ToastContext';
import { Database, Tables } from '@/types/database';
import { Calendar, Clock, MapPin, Users, Ticket, Globe, Building, Phone, Mail, RefreshCw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import SimpleAuthModal from '@/components/auth/SimpleAuthModal';
import AdditionalProfileModal from '@/components/modals/AdditionalProfileModal';
import TicketSelector from '@/components/event/TicketSelector';
import TicketCheckoutFooter from '@/components/event/TicketCheckoutFooter';
import PaymentCheckout from '@/components/payment/PaymentCheckout';
import GatewayRedirectForm from '@/components/payment/GatewayRedirectForm';
import type { RedirectForm } from '@/lib/payment/types';
import { fetchWithAuthRetry } from '@/lib/utils/fetchWithAuthRetry';
import { clearAuthSession } from '@/lib/services/auth';
import { classifyPaymentCompleteResponse, schedulePendingPaymentPoll, handlePaymentCompleteClassification, usePollCancelledRef } from '@/lib/payment/pendingCompletion';

type Content = Tables<'content'>;

interface TicketInfo {
  id: string;
  name: string;
  price: number;
  currency: string | null;
  status: string | null;
  max_quantity: number | null;
  sold_quantity: number | null;
}

interface OrganizerInfo {
  id: string;
  slug: string | null;
  name: string;
  email: string;
  phone: string | null;
  description: string | null;
  image_url: string | null;
  website_url: string | null;
  facebook_url: string | null;
  moz_bpr_number: string | null;
}

export default function BEventPage() {
  const params = useParams();
  const router = useRouter();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const toast = useToast();
  const supabase = createClient();

  const [course, setCourse] = useState<Content | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolled, setEnrolled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [tickets, setTickets] = useState<TicketInfo[]>([]);
  const [programContent, setProgramContent] = useState<string | null>(null);
  const [mozEventTheme, setMozEventTheme] = useState<string | null>(null);
  const [mozStartDate, setMozStartDate] = useState<string | null>(null);
  const [mozEndDate, setMozEndDate] = useState<string | null>(null);
  // BPR number on the event-level moz submission – non-empty means registration is open.
  const [mozBprNumber, setMozBprNumber] = useState<string | null>(null);
  const [organizer, setOrganizer] = useState<OrganizerInfo | null>(null);
  const [ticketPriceRange, setTicketPriceRange] = useState<{ min: number; max: number } | null>(null);
  const [hasTickets, setHasTickets] = useState(false);

  // Modals
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showTicketSelector, setShowTicketSelector] = useState(false);
  const [ticketSelections, setTicketSelections] = useState<Array<{ ticketId: string; quantity: number; price: number; name: string }>>([]);
  const [totalTicketPrice, setTotalTicketPrice] = useState(0);
  const [selectedPromocodeId, setSelectedPromocodeId] = useState<string>();
  const [paymentData, setPaymentData] = useState<{ provider: string; widgetData: Record<string, unknown>; signature?: string; mode?: 'redirect' | 'widget'; redirect?: RedirectForm } | null>(null);
  // Gateway hasn't confirmed the payment yet (202) or we couldn't reach it
  // (503) – see the pendingCompletion module. Never claim success/enrollment
  // until the server confirms.
  const [paymentPending, setPaymentPending] = useState(false);
  const [paymentConfirmDelayed, setPaymentConfirmDelayed] = useState(false);
  const pollCancelledRef = usePollCancelledRef();
  const paymentConfirmingTitleKey = paymentConfirmDelayed ? 'courseDetails.paymentConfirmingDelayedTitle' : 'courseDetails.paymentConfirmingTitle';
  const paymentConfirmingMessageKey = paymentConfirmDelayed ? 'courseDetails.paymentConfirmingDelayed' : 'courseDetails.paymentConfirming';

  useEffect(() => {
    Promise.all([
      fetchEventDetails(),
      fetchTickets(),
      fetchMozData(),
      ...(user ? [checkEnrollment()] : []),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, user]);

  const fetchEventDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('content')
        .select('*')
        .eq('id', params.id as string)
        .single();

      if (error) throw error;
      setCourse(data as Content);
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = useCallback(async () => {
    try {
      const { isWithinWindow } = await import('@/lib/availability/resolveWindow');
      const [{ data, error }, { data: eventTimesRow }] = await Promise.all([
        supabase
          .from('tickets')
          .select('id, name, price, currency, status, max_quantity, sold_quantity, requires_promocode, sale_start_date, sale_end_date, sale_start_anchor, sale_end_anchor')
          .eq('content_id', params.id as string)
          .eq('status', 'available')
          .order('price', { ascending: true }),
        supabase
          .from('content')
          .select('start_date, end_date')
          .eq('id', params.id as string)
          .single(),
      ]);

      if (!error && data && data.length > 0) {
        const eventTimes = {
          start_date: eventTimesRow?.start_date ?? null,
          end_date: eventTimesRow?.end_date ?? null,
        };
        // Only show publicly visible tickets (not promo-only, within sale window)
        const publicTickets = data.filter((t) => {
          if (t.requires_promocode) return false;
          return isWithinWindow(
            {
              from: t.sale_start_date,
              from_anchor: t.sale_start_anchor ?? 'absolute',
              until: t.sale_end_date,
              until_anchor: t.sale_end_anchor ?? 'absolute',
            },
            eventTimes
          );
        });
        setTickets(data as TicketInfo[]);
        setHasTickets(true);
        if (publicTickets.length > 0) {
          const prices = publicTickets.map(t => Number(t.price));
          setTicketPriceRange({ min: Math.min(...prices), max: Math.max(...prices) });
        }
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const fetchMozData = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('moz_submission_data')
        .select('program_content, event_theme, start_date, end_date, bpr_number, organizer_profiles(id, slug, name, email, phone, description, image_url, website_url, facebook_url, moz_bpr_number)')
        .eq('event_id', params.id as string)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        if (data.program_content) setProgramContent(data.program_content);
        if (data.event_theme) setMozEventTheme(data.event_theme);
        if (data.start_date) setMozStartDate(data.start_date);
        if (data.end_date) setMozEndDate(data.end_date);
        if (data.bpr_number) setMozBprNumber(data.bpr_number);
        if (data.organizer_profiles) setOrganizer(data.organizer_profiles as unknown as OrganizerInfo);
      }
    } catch (error) {
      console.error('Error fetching moz data:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  // Returns whether an enrollment was found, so the pending-payment poll
  // knows when to stop.
  const checkEnrollment = async (): Promise<boolean> => {
    if (!user) return false;
    const { data } = await supabase
      .from('enrollments')
      .select('id')
      .eq('user_id', user.id)
      .eq('content_id', params.id as string)
      .maybeSingle();
    setEnrolled(!!data);
    return !!data;
  };

  const handleEnrollment = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!course) return;

    if (hasTickets) {
      setShowTicketSelector(true);
      return;
    }

    // Free enrollment
    setEnrolling(true);
    try {
      const profileResponse = await fetchWithAuthRetry(`/api/courses/${course.id}/profile-completion`);

      if (profileResponse.status === 401) {
        toast.error(t('authModal.authSessionRestoreFailed'), {
          duration: 15000,
          action: { label: t('authModal.authClearSessionAndRetry'), onClick: () => { clearAuthSession(); window.location.reload() } }
        });
        setEnrolling(false);
        return;
      }

      const profileData = await profileResponse.json();

      if (!profileResponse.ok) {
        console.error('Profile check server error:', profileData.error);
        toast.error(t('course.serverErrorTryAgain'));
        setEnrolling(false);
        return;
      }

      if (!profileData.isComplete) {
        setShowProfileModal(true);
        setEnrolling(false);
        return;
      }

      await proceedWithEnrollment();
    } catch (error) {
      console.error('Profile check error:', error);
      toast.error(t('course.networkErrorCheckConnection'));
      setEnrolling(false);
    }
  };

  const proceedWithEnrollment = async () => {
    if (!course || !user) return;
    let startedPayment = false;

    try {
      const needsPayment = ticketSelections.length > 0 && totalTicketPrice > 0;

      if (needsPayment) {
        const response = await fetch('/api/payment/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            courseId: course.id,
            userId: user.id,
            ticketSelections: ticketSelections.map(s => ({ ticketId: s.ticketId, quantity: s.quantity })),
            promocodeId: selectedPromocodeId,
          }),
        });
        const data = await response.json();
        if (response.ok) {
          setPaymentData(data);
          startedPayment = true;
        } else {
          toast.error(data.errorCode ? t(`errors.payment.${data.errorCode}`) : t('errors.general'));
        }
      } else {
        const response = await fetch('/api/enrollments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content_id: course.id,
            ticketSelections,
            promocodeId: selectedPromocodeId,
          }),
        });
        const data = await response.json();
        if (response.ok) {
          setEnrolled(true);
          toast.success(t('event.registerSuccess'));
        } else {
          toast.error(data.error || t('courseDetails.failedToEnroll'));
        }
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      toast.error(t('courseDetails.failedToEnroll'));
    } finally {
      if (!startedPayment) {
        setEnrolling(false);
      }
    }
  };

  const handleTicketSelectionComplete = async () => {
    if (!user) return;
    setShowTicketSelector(false);
    setEnrolling(true);

    try {
      const profileResponse = await fetchWithAuthRetry(`/api/courses/${course?.id}/profile-completion`);

      if (profileResponse.status === 401) {
        toast.error(t('authModal.authSessionRestoreFailed'), {
          duration: 15000,
          action: { label: t('authModal.authClearSessionAndRetry'), onClick: () => { clearAuthSession(); window.location.reload() } }
        });
        setEnrolling(false);
        return;
      }

      const profileData = await profileResponse.json();

      if (!profileResponse.ok) {
        console.error('Profile check server error:', profileData.error);
        toast.error(t('course.serverErrorTryAgain'));
        setEnrolling(false);
        return;
      }

      if (!profileData.isComplete) {
        setShowProfileModal(true);
        setEnrolling(false);
        return;
      }

      await proceedWithEnrollment();
    } catch (error) {
      console.error('Profile check error:', error);
      toast.error(t('course.networkErrorCheckConnection'));
      setEnrolling(false);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return null;
    const d = new Date(date);
    if (language === 'uk') {
      const months = ['січня', 'лютого', 'березня', 'квітня', 'травня', 'червня', 'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'];
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()} р.`;
    }
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatTime = (date: string | null) => {
    if (!date) return null;
    const d = new Date(date);
    const h = d.getHours(), m = d.getMinutes();
    if (h === 0 && m === 0) return null;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const getPriceDisplay = () => {
    if (ticketPriceRange) {
      const { min, max } = ticketPriceRange;
      if (min === max) return min === 0 ? t('courseDetails.free') : `₴${min}`;
      return `₴${min} - ₴${max}`;
    }
    return t('courseDetails.free');
  };

  const getFormatLabel = (type: string | null) => {
    switch (type) {
      case 'online': return t('courseDetails.online');
      case 'in_person': return language === 'uk' ? 'Очно' : 'In Person';
      case 'hybrid': return language === 'uk' ? 'Змішаний' : 'Hybrid';
      default: return t('courseDetails.online');
    }
  };

  const getFormatIcon = (type: string | null) => {
    switch (type) {
      case 'online': return <Globe className="h-5 w-5 text-blue-500" />;
      case 'in_person': return <Building className="h-5 w-5 text-orange-500" />;
      case 'hybrid': return <Globe className="h-5 w-5 text-purple-500" />;
      default: return <Globe className="h-5 w-5 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{t('event.eventNotFound')}</p>
          <Button variant="link" onClick={() => router.push('/')} className="p-0">
            {t('courseDetails.returnToHomepage')}
          </Button>
        </div>
      </div>
    );
  }

  // Use moz_submission_data values for title and dates, fall back to content table
  const displayTitle = mozEventTheme || course.title;
  const displayStartDate = mozStartDate || course.start_date;
  const displayEndDate = mozEndDate || course.end_date;
  // Only show time if using content table dates (moz dates are date-only, no time)
  const startTime = mozStartDate ? null : formatTime(course.start_date);
  const endTime = mozEndDate ? null : formatTime(course.end_date);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Payment Confirming Banner – gateway hasn't confirmed yet (202) or we
          couldn't reach it (503); the webhook/cron will settle it shortly.
          Once the bounded poll gives up (paymentConfirmDelayed), the spinner
          stops and the copy switches to "taking longer than usual" instead of
          implying a poll that's no longer running. */}
      {paymentPending && !enrolled && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <RefreshCw className={`h-6 w-6 text-blue-600 flex-shrink-0 ${paymentConfirmDelayed ? '' : 'animate-spin'}`} />
              <div>
                <h3 className="font-semibold text-blue-800">{t(paymentConfirmingTitleKey)}</h3>
                <p className="text-blue-700 text-sm">{t(paymentConfirmingMessageKey)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment: hosted gateway page (default) or on-page widget (PAYMENT_CHECKOUT_MODE=widget) */}
      {paymentData && paymentData.mode === 'redirect' && paymentData.redirect && (
        <GatewayRedirectForm
          redirect={paymentData.redirect}
          message={t('courseDetails.paymentRedirecting')}
          continueLabel={t('courseDetails.paymentContinue')}
          cancelLabel={t('courseDetails.paymentCancel')}
          onCancel={() => { setPaymentData(null); setEnrolling(false); }}
        />
      )}
      {paymentData && !(paymentData.mode === 'redirect' && paymentData.redirect) && (
        <PaymentCheckout
          provider={paymentData.provider}
          widgetData={paymentData.widgetData}
          signature={paymentData.signature}
          onSuccess={async (response) => {
            setPaymentData(null);

            // Call server to verify signature, create enrollment, send email.
            // Only claim success once the server confirms it – see
            // pendingCompletion.ts for why a non-ok response isn't
            // automatically a failure.
            try {
              const res = await fetchWithAuthRetry('/api/payment/complete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(response),
              });

              const result = await res.json();
              const classification = classifyPaymentCompleteResponse(res, result);

              if (classification === 'success') {
                setEnrolled(true);
                toast.success(t('courseDetails.paymentSuccessful'));
              } else {
                // 'pending': gateway hasn't confirmed yet, or we couldn't
                // reach it – the webhook or the reconciliation cron will
                // settle this. Keep the CTA hidden and poll instead of
                // telling the participant their payment failed.
                if (classification === 'failure') {
                  console.error('Payment complete API failed:', result.error);
                }
                handlePaymentCompleteClassification(classification, {
                  toast,
                  t,
                  setPaymentPending,
                  startPendingPoll: () => schedulePendingPaymentPoll({
                    isCancelled: () => pollCancelledRef.current,
                    checkEnrollment,
                    onResolved: () => { setPaymentPending(false); setPaymentConfirmDelayed(false); },
                    onExhausted: () => setPaymentConfirmDelayed(true),
                  }),
                });
              }
            } catch (err) {
              console.error('Payment complete request failed:', err);
              toast.error(t('courseDetails.failedToEnroll'));
            }

            setEnrolling(false);
          }}
          onError={(error) => {
            console.error('Payment failed:', error);
            toast.error(t('courseDetails.paymentFailed'));
            setPaymentData(null);
            setEnrolling(false);
          }}
          onClose={() => {
            setPaymentData(null);
            setEnrolling(false);
          }}
        />
      )}

      {/* Auth Modal */}
      <SimpleAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode="signup"
      />

      {/* Profile Modal */}
      <AdditionalProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        courseId={course.id}
        courseTitle={course.title}
        onComplete={async () => {
          setShowProfileModal(false);
          await proceedWithEnrollment();
        }}
      />

      {/* Ticket Selector Modal */}
      {showTicketSelector && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowTicketSelector(false); }}
        >
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-lg max-w-2xl w-full max-h-[85vh] sm:max-h-[90vh] flex flex-col">
            <div className="flex-shrink-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between rounded-t-2xl sm:rounded-t-lg relative">
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-2 sm:mt-0">{t('event.registration.selectYourTickets')}</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowTicketSelector(false)} className="p-2 -mr-2">
                ✕
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <TicketSelector
                eventId={course.id}
                onSelectionChange={(selections, totalPrice, promocodeId) => {
                  setTicketSelections(selections);
                  setTotalTicketPrice(totalPrice);
                  setSelectedPromocodeId(promocodeId);
                }}
                initialPromoCode={typeof window !== 'undefined' ? new URL(window.location.href).searchParams.get('promo') || undefined : undefined}
              />
            </div>
            <TicketCheckoutFooter
              totalPrice={totalTicketPrice}
              hasSelection={ticketSelections.length > 0}
              onConfirm={() => handleTicketSelectionComplete()}
            />
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Provider Name */}
        {organizer && (
          <p className="text-lg text-gray-900 mb-2">
            <span className="font-bold">Сайт провайдера:</span>{' '}
            {organizer.moz_bpr_number ? (
              <a
                href={`/org/${organizer.slug || organizer.id}`}
                className="text-gray-900 no-underline hover:text-blue-600 hover:underline"
              >
                {organizer.name}
              </a>
            ) : (
              organizer.name
            )}
          </p>
        )}

        {/* Event Title */}
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">{displayTitle}</h1>

        {/* Info Grid */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100 mb-8">
          {/* Date & Time */}
          {displayStartDate && (
            <div className="flex items-start gap-4 p-4">
              <Calendar className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium text-gray-900">
                  {formatDate(displayStartDate)}
                  {displayEndDate && displayStartDate?.slice(0, 10) !== displayEndDate?.slice(0, 10) && (
                    <> – {formatDate(displayEndDate)}</>
                  )}
                </div>
                {(startTime || endTime) && (
                  <div className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {startTime}{startTime && endTime && ' – '}{endTime}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Format */}
          <div className="flex items-start gap-4 p-4">
            {getFormatIcon(course.event_format)}
            <div className="font-medium text-gray-900">
              {getFormatLabel(course.event_format)}
            </div>
          </div>

          {/* Location */}
          {course.location && (course.event_format === 'in_person' || course.event_format === 'hybrid') && (
            <div className="flex items-start gap-4 p-4">
              <MapPin className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="font-medium text-gray-900">{course.location}</div>
            </div>
          )}

          {/* Participants */}
          {course.show_participant_count !== false && course.enrollment_count !== null && course.enrollment_count > 0 && (
            <div className="flex items-start gap-4 p-4">
              <Users className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <div className="font-medium text-gray-900">
                {course.enrollment_count} {t('event.registered')}
                {course.max_participants && (
                  <span className="text-gray-500 text-sm ml-1">/ {course.max_participants}</span>
                )}
              </div>
            </div>
          )}

          {/* Price */}
          <div className="flex items-start gap-4 p-4">
            <Ticket className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
            <div className="font-bold text-lg text-gray-900">{getPriceDisplay()}</div>
          </div>

        </div>

        {/* Registration Button */}
        <div className="mb-8">
          {enrolled ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <p className="font-semibold text-green-800">{t('courseDetails.youAreRegisteredEvent')}</p>
            </div>
          ) : paymentPending ? null : mozBprNumber && mozBprNumber.trim() ? (
            <Button
              onClick={handleEnrollment}
              disabled={enrolling}
              size="lg"
              className="w-full text-lg py-6"
            >
              {enrolling ? t('courseDetails.processing') : t('courseCard.registerForEvent')}
            </Button>
          ) : (
            <Button
              disabled
              size="lg"
              className="w-full text-lg py-6"
            >
              {t('courseCard.registrationOpensSoon')}
            </Button>
          )}
        </div>

        {/* Program Content from moz_submission_data */}
        {programContent && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t('event.tabs.program') || 'Program'}</h2>
            <div
              className="text-gray-700 leading-relaxed rich-text-content"
              dangerouslySetInnerHTML={{ __html: programContent }}
            />
          </div>
        )}

        {/* Organizer Contact Card */}
        {organizer && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {language === 'uk' ? 'Інформація про провайдера' : 'Provider Information'}
            </h2>
            <div className="flex flex-col sm:flex-row gap-6">
              {organizer.image_url && (
                <div className="flex-shrink-0">
                  <img
                    src={organizer.image_url}
                    alt={organizer.name}
                    className="w-24 h-24 rounded-lg object-cover border border-gray-200"
                  />
                </div>
              )}
              <div className="flex-1 space-y-3">
                <h3 className="text-lg font-semibold text-gray-900">{organizer.name}</h3>
                {organizer.description && (
                  <div className="text-sm text-gray-600 [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-700 [&_strong]:font-semibold [&_p]:mb-1 [&_p:last-child]:mb-0">
                    <ReactMarkdown>{organizer.description}</ReactMarkdown>
                  </div>
                )}
                {organizer.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <a href={`mailto:${organizer.email}`} className="hover:text-blue-600 underline">{organizer.email}</a>
                  </div>
                )}
                {organizer.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <a href={`tel:${organizer.phone}`} className="hover:text-blue-600">{organizer.phone}</a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
